import { VerifyAuthResponse } from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import jwtDecode from 'jwt-decode';
import * as oauth from 'oauth4webapi';
import {
  hashCode,
  AuthStorage,
  DecodedOIDCIDToken,
  getLastVisitedPage,
  isWeaveTokenActive,
  localStorageHelper,
  OidcClientConfig,
  SignInMethod,
} from '@frontend/auth-helpers';
import { sentry } from '@frontend/tracking';
import { storeTimeToRedirectToAuth } from '../logging/time-to-redirect-logger';
import { navigateToUrl } from '../utils/navigateToUrl';
import { AuthFlow, SignInProps, SignOutProps, ClientTokens, HandleCallbackProps } from './AuthFlow';

const s256 = 'S256';
const tokenEndpointAuthMethod = 'none';

// implementation based on the example given by oauth4webapi:
// https://github.com/panva/oauth4webapi/blob/main/examples/public.ts

export class OidcClient implements AuthFlow {
  private config: OidcClientConfig;
  private as: oauth.AuthorizationServer | undefined;

  constructor(config: OidcClientConfig) {
    this.config = config;
  }

  public async signIn(props?: SignInProps): Promise<VerifyAuthResponse> {
    const clientId = props?.clientId || this.config.clientID;
    const redirectUri = props?.redirectUri || this.config.redirectUri;
    if (!clientId || !redirectUri) {
      throw new Error('oidc client not configured correctly, must provide both clientId and redirectUri');
    }

    const as = await this.getAuthenticationServer();
    // check if the server supports PKCE
    if (as.code_challenge_methods_supported?.includes(s256) !== true) {
      // This assumes S256 PKCE support is signalled
      // If it isn't supported, random `nonce` must be used for CSRF protection.
      throw new Error();
    }
    const code_verifier = oauth.generateRandomCodeVerifier();
    const code_challenge = await oauth.calculatePKCECodeChallenge(code_verifier);
    const code_challenge_method = s256;
    // store the code_verifier in local storage for later use
    localStorage.setItem('oidc.code_verifier', code_verifier);

    const authorizationUrl = new URL(as.authorization_endpoint!);
    authorizationUrl.searchParams.set('client_id', clientId);
    authorizationUrl.searchParams.set('code_challenge', code_challenge);
    authorizationUrl.searchParams.set('code_challenge_method', code_challenge_method);
    authorizationUrl.searchParams.set('redirect_uri', redirectUri.toString());
    authorizationUrl.searchParams.set('response_type', 'code');
    authorizationUrl.searchParams.set('scope', 'openid email');
    authorizationUrl.searchParams.set('state', this.generateStateHash());

    // this perf log measures the time it takes to:
    // 1. go to NWX
    // 2. whole app loads
    // 3. token is expired so redirect to auth.getweave
    // this is the lastest possible moment to log that
    if (
      props?.meta?.source === 'auth_check' &&
      !window.location.origin.includes('auth') &&
      !this.isUserAuthenticated()
    ) {
      storeTimeToRedirectToAuth(props?.meta?.authRedirectExpGroup);
    }

    navigateToUrl(authorizationUrl.toString());
    return Promise.resolve({});
  }

  // Not to be used as a cryptographic hash function.
  private generateStateHash(): string {
    const desiredPage = getLastVisitedPage();
    const randomNumber = Math.random() * 100000;
    return 'st_' + hashCode(desiredPage) + '_' + randomNumber.toString() + '_' + new Date().getTime();
  }

  private async getAuthenticationServer() {
    if (this.as) {
      return Promise.resolve(this.as);
    }
    const discoveryResponse = await oauth.discoveryRequest(this.config.issuer);
    return oauth.processDiscoveryResponse(this.config.issuer, discoveryResponse);
  }

  public async signOut(props?: SignOutProps): Promise<void> {
    const as = await this.getAuthenticationServer();

    if (!as.end_session_endpoint) {
      if (props?.logoutRedirect) {
        navigateToUrl(props?.logoutRedirect);
      } else {
        sentry.error({
          error: 'oidc.signout - no end_session_endpoint, redirect to /',
          topic: 'auth',
          team: 'users-infra',
          sampleRate: 1,
        });
        navigateToUrl('/');
      }
      return;
    }

    const oidc_token = localStorageHelper.get<{ accessToken: string; IDToken: string }>(AuthStorage.oidc_token_storage);

    const redirectUri = `${window.location.origin}`;
    const params = new URLSearchParams();

    // Include id_token_hint if available (expired tokens are valid for logout)
    // post_logout_redirect_uri requires id_token_hint to be sent
    if (oidc_token?.IDToken) {
      params.set('id_token_hint', oidc_token.IDToken);
      params.set('post_logout_redirect_uri', redirectUri);
    }

    localStorageHelper.delete(AuthStorage.oidc_token_storage);
    navigateToUrl(`${as.end_session_endpoint}?${params.toString()}`, { replace: true });
  }

  public getTokens(): Promise<ClientTokens> {
    const tokens = localStorageHelper.get<ClientTokens>(AuthStorage.oidc_token_storage);
    return Promise.resolve(tokens || {});
  }

  public async handleCallback(props?: HandleCallbackProps): Promise<ClientTokens> {
    const clientId = props?.clientId || this.config.clientID;
    const redirectUri = props?.redirectUri || this.config.redirectUri;
    if (!clientId || !redirectUri) {
      throw new Error('oidc client not configured correctly, must provide both clientId and redirectUri');
    }

    const client: oauth.Client = {
      client_id: clientId,
      token_endpoint_auth_method: tokenEndpointAuthMethod,
    };
    const as = await this.getAuthenticationServer();
    const code_verifier = localStorage.getItem('oidc.code_verifier');
    if (!code_verifier) {
      throw new Error('Code verifier not found');
    }
    // validate that we are on the expected URL
    const currentUrl: URL = new URL(window.location.href);
    const params = oauth.validateAuthResponse(as, client, currentUrl, oauth.skipStateCheck); // might be able to use state to redirect to the desired page
    if (oauth.isOAuth2Error(params)) {
      throw new Error('Could not validate callback parameters'); // Handle OAuth 2.0 redirect error
    }
    const response = await oauth.authorizationCodeGrantRequest(
      as,
      client,
      params,
      redirectUri.toString(),
      code_verifier
    );

    const result = await oauth.processAuthorizationCodeOpenIDResponse(as, client, response);
    if (oauth.isOAuth2Error(result)) {
      throw new Error("Could not process identity provider's response"); // Handle OAuth 2.0 response body error
    }
    // Kept this commented so I can remember how to get the claims from the token
    // const claims = oauth.getValidatedIdTokenClaims(result);

    // store access token and id token in local storage
    const clientTokens: ClientTokens = {
      accessToken: (result as oauth.OpenIDTokenEndpointResponse).access_token,
      IDToken: (result as oauth.OpenIDTokenEndpointResponse).id_token,
    };

    localStorageHelper.create(AuthStorage.oidc_token_storage, clientTokens);

    return clientTokens;
  }

  public getSignInMethod(): Promise<SignInMethod> {
    return Promise.resolve('oidc');
  }

  public isUserAuthenticated(): boolean {
    const oidcTokens = localStorageHelper.get<ClientTokens>(AuthStorage.oidc_token_storage);
    const weaveTokenActive = isWeaveTokenActive();
    const oidcTokenActive = !!oidcTokens?.IDToken && !this.isOIDCTokenExpired(oidcTokens.IDToken);
    return weaveTokenActive || oidcTokenActive;
  }

  private isOIDCTokenExpired(IDToken: string): boolean {
    try {
      const decodedToken = jwtDecode<DecodedOIDCIDToken>(IDToken);
      const expirationDate = new Date(0);
      expirationDate.setUTCSeconds(decodedToken.exp);
      return expirationDate < new Date();
    } catch (e) {
      return true;
    }
  }
}
